/// <amd-module name="Wit/Search/PrefetchContent/PrefetchInstantSearchContentService" />
import { ITfsPageService } from "Tfs/Platform/Page";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IVssPageContext, IVssPersistantService, IVssService, ServiceOptions, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssSearchService } from "VSS/Platform/Search";
import { SecuredGenericData } from "Wit/Common/Generated/Contracts";

export interface IPrefetchInstantSearchContentService extends IVssService, IVssPersistantService {
    readonly recentWorkItemsCache: Promise<SecuredGenericData[] | undefined>;
    readonly recentWikiPagesCache: Promise<any[] | undefined>;
    fetchInstantSearchContent(): void;
    cacheRecentWorkItemsData(): void;
}

/**
 * Service to prefetch instant search scripts and recency data.
 */
export class PrefetchInstantSearchContentService extends VssService implements IPrefetchInstantSearchContentService {
    private get isProjectContext(): boolean | undefined {
        if (this._isProjectContext == undefined) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            this._isProjectContext = tfsPageData && tfsPageData.project ? true : false;
        }
        return this._isProjectContext;
    }

    public get menuContributionId(): string {
        if (!this._menuContributionId) {
            if (this.isProjectContext) {
                // project context
                this._menuContributionId = "ms.vss-work-web.instant-search-project-menu";
            } else {
                // collection context
                this._menuContributionId = "ms.vss-work-web.instant-search-collection-menu";
            }
        }

        return this._menuContributionId;
    }

    private _isProjectContext: boolean | undefined;
    private _menuContributionId: string;
    private _recentWorkItemsCache: SecuredGenericData[] | undefined;
    private _recentWikiPagesCache: any[] | undefined;
    private _isWorkItemProviderSelected = false;
    private _isWikiProviderSelected = false;
    private _shouldPrefetch = false;
    private _areInstantSearchScriptsFetched = false;
    private _throttledCacheRecentWorkItemsData: () => void;
    private _throttledCacheRecentWikiPagesData: () => void;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initialize();
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
        this._initialize();
    }

    public fetchInstantSearchContent(): void {
        if (this._shouldPrefetch) {
            this._shouldPrefetch = false;
            if (!this._areInstantSearchScriptsFetched) {
                this._areInstantSearchScriptsFetched = true;
                this._fetchInstantSearchScripts();
                this._fetchInstantSearchMenuContribution();
            }

            if (!this._recentWorkItemsCache && this._isWorkItemProviderSelected) {
                this._fetchRecentWorkItemsData();
            }

            if (!this._recentWikiPagesCache && this._isWikiProviderSelected){
                this._fetchRecentWikiPagesData();
            }
        }
    }

    public cacheRecentWorkItemsData(): void {
        this._throttledCacheRecentWorkItemsData();
    }

    public cacheRecentWikiPagesData(): void {
        this._throttledCacheRecentWikiPagesData();
    }

    public get recentWorkItemsCache(): Promise<SecuredGenericData[] | undefined> {
        if (!this._recentWorkItemsCache) {
            return this._fetchRecentWorkItemsData();
        }

        return Promise.resolve(this._recentWorkItemsCache);
    }

    public get recentWikiPagesCache(): Promise<any[] | undefined> {
        if (!this._recentWikiPagesCache){
            return this._fetchRecentWikiPagesData();
        }

        return Promise.resolve(this._recentWikiPagesCache)
    }

    private _initialize(): void {
        const searchService = this.pageContext.getService<IVssSearchService>("IVssSearchService");
        const selectedProviderId = searchService.getSelectedProvider();
        this._isWorkItemProviderSelected =
            selectedProviderId === "ms.vss-work-web.workitem-project-search-provider" ||
            selectedProviderId === "ms.vss-work-web.workitem-collection-search-provider";

        this._isWikiProviderSelected = 
            selectedProviderId === "ms.vss-wiki-web.wiki-project-search-provider";
        this._shouldPrefetch = this._isWorkItemProviderSelected || this._isWikiProviderSelected;
        this._throttledCacheRecentWorkItemsData = new TimerManagement().throttle(this._fetchRecentWorkItemsData, 100);
        this._throttledCacheRecentWikiPagesData = new TimerManagement().throttle(this._fetchRecentWikiPagesData, 100);
    }

    private _fetchInstantSearchScripts(): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getContributionsAsync(["ms.vss-work-web.search-content"]);
        contributionService.getContributionsAsync(["ms.vss-wiki-web.wiki-search-content"]);
    }

    private _fetchInstantSearchMenuContribution(): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getContributionAsync(this.menuContributionId);
    }

    private _fetchRecentWorkItemsData = (): Promise<SecuredGenericData[] | undefined> => {
        const dataProviderId = "ms.vss-work-web.workitem-search-recent-activity-data-provider";
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<SecuredGenericData[]>(dataProviderId, undefined, true).then(data => {
            this._recentWorkItemsCache = data;
            return this._recentWorkItemsCache;
        });
    };

    private _fetchRecentWikiPagesData = (): Promise<any[] | undefined> => {
        const dataProviderId = "ms.vss-wiki-web.wikipage-recent-activity-data-provider";
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<SecuredGenericData[]>(dataProviderId, undefined, true).then(data => {
            this._recentWikiPagesCache = data;
            return this._recentWikiPagesCache;
        });
    }
}

// Registering as a Persistant service in order to ensure that the cache is preserved after navigating via FPS.
Services.add("IPrefetchInstantSearchContentService", { serviceFactory: PrefetchInstantSearchContentService, options: ServiceOptions.Persistant });
